import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { countryName, flagEmoji } from "../../i18n/countries";
import { formatMoney, intlLocale } from "../../lib/format";

/**
 * The parts both control panels are drawn from.
 *
 * There are two of them — sales and procurement — and they are the same screen read from the two
 * ends of the same business: one says what customers owe Bankom, the other what Bankom owes its
 * co-operatives. A director moving between them must not have to re-learn where a figure sits, so
 * neither screen owns its own chart, card or table; both take them from here.
 *
 * That is also the cheap guarantee: a change to the tooltip, the aging colours or the bar
 * animation lands on both at once, and the two cannot drift into looking like different products.
 *
 * Nothing here knows which ledger it is drawing. What a bar means, which currency a number is in
 * and where a row leads are the caller's to say.
 */

// ---------------------------------------------------------------------------
// Shared formatting
// ---------------------------------------------------------------------------

/** „2026-09" → „sep" na jeziku korisnika; osa grafikona nosi ime meseca, ne šifru. */
export function nazivMeseca(bucket: string, locale: string): string {
  const [g, m] = bucket.split("-").map(Number);
  if (!g || !m) return bucket;
  return new Date(g, m - 1, 1).toLocaleDateString(intlLocale(locale), { month: "short" });
}

/** Skraćen broj za osu grafikona: „1.2M", „340k". Osa nema mesta za ceo iznos. */
export function kratkiBroj(n: number): string {
  if (n < 0) return "-" + kratkiBroj(-n);
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 10_000) return Math.round(n / 1000) + "k";
  if (n >= 1000) return (n / 1000).toFixed(1) + "k";
  return n.toString();
}

export function relTime(iso: string, locale: string, t: TFunction): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return t("orders.time.justNow", "just now");
  if (min < 60) return t("orders.time.minAgo", "{{count}} min ago", { count: min });
  const h = Math.floor(min / 60);
  if (h < 24) return t("orders.time.hrAgo", "{{count}} hr ago", { count: h });
  const d = Math.floor(h / 24);
  if (d < 7) return t("orders.time.dAgo", "{{count}}d ago", { count: d });
  return new Date(iso).toLocaleDateString(locale);
}

// ---------------------------------------------------------------------------
// Number ticker
// ---------------------------------------------------------------------------

export function useTween(target: number, durationMs = 800, delayMs = 0): number {
  const [v, setV] = useState(0);
  const startRef = useRef<number | null>(null);
  useEffect(() => {
    let raf = 0;
    const startVal = 0;
    const begin = performance.now() + delayMs;
    const tick = (now: number) => {
      if (now < begin) { raf = requestAnimationFrame(tick); return; }
      if (startRef.current == null) startRef.current = now;
      const t = Math.min(1, (now - begin) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setV(startVal + (target - startVal) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, delayMs]);
  return v;
}

// ---------------------------------------------------------------------------
// Tooltip helper
// ---------------------------------------------------------------------------

export function useTooltip() {
  const [tip, setTip] = useState<{ text: string; x: number; y: number } | null>(null);
  const onMove = (text: string) => (e: React.MouseEvent) => {
    setTip({ text, x: e.clientX + 12, y: e.clientY - 28 });
  };
  const onLeave = () => setTip(null);
  const node = tip ? (
    <div className="sd-tooltip" style={{ "--tip-x": `${tip.x}px`, "--tip-y": `${tip.y}px` } as CSSProperties}>{tip.text}</div>
  ) : null;
  return { onMove, onLeave, node };
}

// ---------------------------------------------------------------------------
// KPI card with tweened value + animated sparkline
// ---------------------------------------------------------------------------

export function KpiCard({
  delay = 0, label, value, format, delta, deltaLabel, deltaTone, note, spark, color = "var(--brand-primary)", progressPct,
}: {
  delay?: number;
  label: string;
  value: number;
  format: (v: number) => string;
  delta?: number;
  deltaLabel?: string;
  deltaTone?: "ok" | "warn" | null;
  /**
   * What the value leaves out, in a neutral tone: it is context, not good or bad news. More than one
   * such line can be passed as elements, each in its own block.
   */
  note?: ReactNode;
  spark?: number[];
  color?: string;
  progressPct?: number;
}) {
  const tweened = useTween(value, 900, delay * 90);
  const tone =
    deltaTone === "ok" ? "var(--ok)" :
    deltaTone === "warn" ? "var(--warn)" :
    delta !== undefined ? (delta >= 0 ? "var(--ok)" : "var(--warn)") : "var(--ink-3)";
  return (
    <div className="card sd-card" style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 6, position: "relative", animationDelay: `${delay * 60}ms` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ink-3)", letterSpacing: ".04em", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: "var(--ink)", fontVariantNumeric: "tabular-nums" }}>{format(tweened)}</div>
      <div style={{ fontSize: 12, color: tone }}>
        {delta !== undefined && (delta >= 0 ? `▲ ${delta}%` : `▼ ${Math.abs(delta)}%`)}
        {deltaLabel}
      </div>
      {note && <div style={{ fontSize: 11, color: "var(--ink-3)" }}>{note}</div>}
      {spark && <Sparkline values={spark} color={color} height={28} />}
      {progressPct !== undefined && (
        <div className="sd-hbar-track" style={{ height: 6 }}>
          <div className="sd-hbar-fill" style={{ background: color, ["--sd-w" as any]: `${Math.min(progressPct, 100)}%` }} />
        </div>
      )}
    </div>
  );
}

export function Sparkline({ values, color, height = 32 }: { values: number[]; color: string; height?: number }) {
  if (values.length === 0) return null;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = Math.max(max - min, 1);
  const w = 240;
  const h = height;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y = h - ((v - min) / range) * h;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  // approximate length for stroke-dash animation
  const len = w * 1.5;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: "100%", height, display: "block" }}>
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill={color} opacity={0.12} />
      <polyline className="sd-spark-line" points={pts} fill="none" stroke={color} strokeWidth={1.6}
                strokeLinecap="round" strokeLinejoin="round" style={{ ["--sd-len" as any]: len }} />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Bar chart with hover tooltips + animation
// ---------------------------------------------------------------------------

export function BarChart({ data, color, formatValue }: { data: { label: string; value: number }[]; color: string; formatValue: (v: number) => string }) {
  const [hover, setHover] = useState<number | null>(null);
  const tip = useTooltip();
  if (data.length === 0) return null;
  const values = data.map((d) => d.value);
  // A month can fall below zero: on the procurement board a cancellation or a return takes from the
  // month it was booked in, so a month with more taken back than bought is a column down from a zero
  // line. With no such month the axis, ticks and columns are exactly what they were.
  const min = Math.min(...values, 0);
  const max = min < 0 ? Math.max(...values, 0) : Math.max(...values, 1);
  const w = 600;
  const h = 200;
  const pad = { top: 12, right: 8, bottom: 24, left: 44 };
  const innerW = w - pad.left - pad.right;
  const innerH = h - pad.top - pad.bottom;
  const barW = innerW / data.length;
  const yOf = (v: number) => pad.top + ((max - v) / (max - min)) * innerH;
  const zeroY = yOf(0);
  const ticks = 4;
  const tickValues = min < 0
    ? apart([0, ...(max > 0 ? [max, max / 2] : []), min], yOf, 12)
    : Array.from({ length: ticks + 1 }, (_, i) => max * (1 - i / ticks));
  return (
    <div style={{ position: "relative" }}>
      {tip.node}
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: "100%", height: 200, display: "block" }}>
        {tickValues.map((value, i) => {
          const y = yOf(value);
          // Below-zero months need the zero line told apart from the gridlines around it.
          const zeroLine = min < 0 && value === 0;
          return (
            <g key={i}>
              <line x1={pad.left} x2={w - pad.right} y1={y} y2={y}
                    stroke={zeroLine ? "var(--ink-3)" : "var(--border)"} strokeWidth={zeroLine ? 1 : 0.5} />
              <text x={pad.left - 6} y={y + 4} textAnchor="end" fontSize="10" fill="var(--ink-3)">{kratkiBroj(Math.round(value))}</text>
            </g>
          );
        })}
        {data.map((d, i) => {
          // A column below zero is kept at least two units tall, so a small negative month still shows
          // and still carries its tooltip.
          const height = d.value < 0 ? Math.max(yOf(d.value) - zeroY, 2) : zeroY - yOf(d.value);
          const x = pad.left + i * barW + barW * 0.15;
          const y = d.value < 0 ? zeroY : yOf(d.value);
          const width = barW * 0.7;
          return (
            <g key={`${d.label}-${i}`}>
              <rect
                className={`sd-bar ${hover !== null && hover !== i ? "sd-dim" : ""}`}
                x={x} y={y} width={width} height={height} rx={3} fill={color}
                style={{ animationDelay: `${i * 50}ms` }}
                onMouseEnter={(e) => { setHover(i); tip.onMove(`${d.label}: ${formatValue(d.value)}`)(e); }}
                onMouseMove={tip.onMove(`${d.label}: ${formatValue(d.value)}`)}
                onMouseLeave={() => { setHover(null); tip.onLeave(); }}
              />
              <text x={pad.left + i * barW + barW / 2} y={h - 6} textAnchor="middle" fontSize="10" fill="var(--ink-3)">{d.label}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/**
 * Tick values in the order given, each dropped when its label would sit within `gap` units of one
 * already kept. The zero line goes first, so a small negative month never pushes the 0 off the axis.
 */
function apart(values: number[], yOf: (v: number) => number, gap: number): number[] {
  const kept: number[] = [];
  for (const v of values) {
    if (kept.every((k) => Math.abs(yOf(k) - yOf(v)) >= gap)) kept.push(v);
  }
  return kept;
}

// ---------------------------------------------------------------------------
// Donut with hover, click, animation
// ---------------------------------------------------------------------------

export function Donut({
  slices, centerLabel, centerSub, highlight, onSliceClick, formatValue = String,
}: {
  slices: { label: string; value: number; color: string }[];
  centerLabel: string;
  centerSub: string;
  highlight?: string | null;
  onSliceClick?: (label: string) => void;
  /** Krug nosi broj dokumenata, ne iznos; oblik vrednosti zato bira pozivalac. */
  formatValue?: (v: number) => string;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const tip = useTooltip();
  const total = slices.reduce((s, x) => s + x.value, 0) || 1;
  const r = 70;
  const cx = 90;
  const cy = 90;
  let acc = 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
      {tip.node}
      <svg width={180} height={180} viewBox="0 0 180 180">
        {slices.map((s, i) => {
          const start = (acc / total) * 2 * Math.PI - Math.PI / 2;
          acc += s.value;
          const end = (acc / total) * 2 * Math.PI - Math.PI / 2;
          const large = end - start > Math.PI ? 1 : 0;
          const x1 = cx + r * Math.cos(start);
          const y1 = cy + r * Math.sin(start);
          const x2 = cx + r * Math.cos(end);
          const y2 = cy + r * Math.sin(end);
          const dim = (highlight && highlight !== s.label) || (hover && hover !== s.label);
          return (
            <path key={s.label}
              className={`sd-donut-slice ${dim ? "sd-dim" : ""}`}
              d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`}
              fill={s.color}
              style={{ animationDelay: `${i * 80}ms` }}
              onMouseEnter={(e) => { setHover(s.label); tip.onMove(`${s.label}: ${formatValue(s.value)}`)(e); }}
              onMouseMove={tip.onMove(`${s.label}: ${formatValue(s.value)}`)}
              onMouseLeave={() => { setHover(null); tip.onLeave(); }}
              onClick={() => onSliceClick?.(s.label)}
            />
          );
        })}
        <circle cx={cx} cy={cy} r={r * 0.62} fill="var(--surface)" pointerEvents="none" />
        <text x={cx} y={cy - 2} textAnchor="middle" fontSize="14" fontWeight="700" fill="var(--ink)" pointerEvents="none">{centerLabel}</text>
        <text x={cx} y={cy + 14} textAnchor="middle" fontSize="10" fill="var(--ink-3)" pointerEvents="none">{centerSub}</text>
      </svg>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
        {slices.map((s) => {
          const dim = (highlight && highlight !== s.label) || (hover && hover !== s.label);
          return (
            <li key={s.label} style={{
              display: "flex", alignItems: "center", gap: 8,
              opacity: dim ? 0.45 : 1, cursor: "pointer", transition: "opacity .12s",
            }} onClick={() => onSliceClick?.(s.label)}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color }} />
              <span style={{ flex: 1, color: "var(--ink-2)" }}>{s.label}</span>
              <span style={{ color: "var(--ink-3)", fontVariantNumeric: "tabular-nums" }}>{formatValue(s.value)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Horizontal bars with width animation + hover tooltip
// ---------------------------------------------------------------------------

export function HorizontalBars({ data, max, format }: { data: { label: string; value: number }[]; max: number; format: (n: number) => string }) {
  const tip = useTooltip();
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
      {tip.node}
      {data.map((d, i) => {
        const pct = Math.max(2, (d.value / max) * 100);
        return (
          <li key={`${d.label}-${i}`} className="sd-hbar-row"
              onMouseMove={tip.onMove(`${d.label}: ${format(d.value)}`)}
              onMouseLeave={tip.onLeave}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 2 }}>
              <span style={{ color: "var(--ink-2)" }}>{d.label}</span>
              <span style={{ color: "var(--ink-3)", fontVariantNumeric: "tabular-nums" }}>{format(d.value)}</span>
            </div>
            <div className="sd-hbar-track">
              <div className="sd-hbar-fill" style={{
                ["--sd-w" as any]: `${pct}%`,
                animationDelay: `${i * 60}ms`,
              }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** Spisak sa zastavicom i imenom države, kakav obe table crtaju. */
export function countryBars(
  rows: { label: string; value: number }[],
  locale: string,
): { label: string; value: number }[] {
  return rows.map((c) => ({ label: `${flagEmoji(c.label)} ${countryName(c.label, locale)}`, value: c.value }));
}

// ---------------------------------------------------------------------------
// Ledger table — sortable, with aging bucket colour
// ---------------------------------------------------------------------------

/** Jedan red duga, sa bilo koje strane knjige: kupac koji duguje ili kooperant kome se duguje. */
export type LedgerRow = {
  companyId: string;
  name: string;
  country: string | null;
  overdueEur: number;
  oldestDays: number | null;
};

/**
 * Ko duguje najviše od onoga što je već dospelo — ili kome se najviše duguje.
 *
 * Red je firma, ne faktura: partner sa dvadeset dospelih računa je jedan razgovor, a ne dvadeset
 * redova. „Najstarije" je najstariji dospeli račun te firme, jer se po njemu vodi eskalacija.
 *
 * Dugmeta za opomenu nema: opomena se šalje iz finansija, gde stoji ceo dokument, a ne sa kontrolne
 * table, gde stoji samo zbir.
 *
 * Prva kolona i veza na red zavise od strane knjige, pa ih bira pozivalac. Pragovi starosti ne:
 * dva ekrana ne smeju da seku dug različito.
 */
export function LedgerTable({
  rows, locale, companyLabel, hrefFor,
}: {
  rows: LedgerRow[];
  locale: string;
  companyLabel: string;
  hrefFor: (row: LedgerRow) => string;
}) {
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState<"days" | "amount">("amount");
  // An unknown age sorts last: it is not zero days, and it is not the oldest either.
  const sorted = [...rows].sort((a, b) =>
    sortBy === "days"
      ? (b.oldestDays ?? -1) - (a.oldestDays ?? -1)
      : b.overdueEur - a.overdueEur,
  );
  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 10, fontSize: 11, color: "var(--ink-3)" }}>
        <span style={{ fontWeight: 600, alignSelf: "center" }}>{t("dashboard.sales.debtors.sortLabel", "Poređaj:")}</span>
        <button type="button" className="sd-tab-btn" aria-pressed={sortBy === "amount"} onClick={() => setSortBy("amount")}>
          {t("finance.payments.amount", "Iznos")}
        </button>
        <button type="button" className="sd-tab-btn" aria-pressed={sortBy === "days"} onClick={() => setSortBy("days")}>
          {t("dashboard.sales.debtors.sortDays", "Dana kašnjenja")}
        </button>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: "left", color: "var(--ink-3)", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em" }}>
            <th style={{ padding: "6px 8px" }}>{companyLabel}</th>
            <th style={{ padding: "6px 8px", textAlign: "right" }}>{t("finance.kpi.overdue", "Dospelo")}</th>
            <th style={{ padding: "6px 8px", textAlign: "right" }}>{t("dashboard.sales.debtors.oldest", "Najstarije")}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((d, i) => {
            const dana = d.oldestDays ?? 0;
            // Isti pragovi kao na ekranu finansija; dva ekrana ne smeju da seku dug različito.
            const tone = dana > 120 ? "var(--err)" : dana > 30 ? "var(--warn)" : "var(--info)";
            const bg = dana > 120 ? "var(--err-bg)" : dana > 30 ? "var(--warn-bg)" : "var(--info-bg)";
            // No age when the whole overdue amount is the opening balance: its date is the day of the
            // carry, not the day the debt arose, so the cell says nothing rather than a made-up figure.
            const nepoznato = d.oldestDays == null;
            return (
              <tr key={d.companyId}
                  className="sd-card"
                  style={{ animationDelay: `${i * 40}ms`, borderTop: "1px solid var(--border)" }}>
                <td style={{ padding: "8px", fontWeight: 600, color: "var(--ink)" }}>
                  <Link to={hrefFor(d)} style={{ color: "inherit", textDecoration: "none" }}>
                    {d.country ? `${flagEmoji(d.country)} ` : ""}{d.name}
                  </Link>
                </td>
                <td style={{ padding: "8px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                  {formatMoney(d.overdueEur, "EUR", locale, { maximumFractionDigits: 0 })}
                </td>
                <td style={{ padding: "8px", textAlign: "right" }}>
                  {nepoznato ? (
                    <span title={t("dashboard.sales.debtors.ageUnknown", "Samo prenos iz ranijih godina: dan nastanka duga nije poznat")}
                          style={{ color: "var(--ink-3)", fontSize: 11 }}>
                      —
                    </span>
                  ) : (
                    <span style={{
                      padding: "2px 8px", borderRadius: 999,
                      background: bg, color: tone,
                      fontSize: 11, fontWeight: 700,
                    }}>
                      {t("dashboard.sales.debtors.overdueDays", "{{count}} d", { count: dana })}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="card sd-card">
      <div className="card-h" style={{ padding: "12px 18px" }}>
        <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {title}
        </h3>
        {action}
      </div>
      <div className="card-b">{children}</div>
    </div>
  );
}

export function Muted({ children }: { children: React.ReactNode }) {
  return <div style={{ color: "var(--ink-3)", fontSize: 13, padding: "8px 0" }}>{children}</div>;
}

export const kpiStrip: React.CSSProperties = {
  display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12,
};
export const actionLink: React.CSSProperties = {
  fontSize: 12, color: "var(--brand-primary)", textDecoration: "none", fontWeight: 600,
};
export const reviewList: React.CSSProperties = {
  listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6,
};
export const reviewItem: React.CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
  padding: "10px 12px", background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: 8,
};

export function reviewStatusChip(kind: "submit" | "review"): React.CSSProperties {
  const palette = kind === "submit"
    ? { bg: "var(--warn-bg)", fg: "var(--warn)" }
    : { bg: "var(--info-bg)", fg: "var(--info)" };
  return {
    background: palette.bg, color: palette.fg,
    padding: "2px 8px", borderRadius: 999,
    fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
  };
}
